import { GridReadyEvent } from 'ag-grid-community';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Order, Product } from '@compumark/order-management-client';
import { Observable } from 'rxjs';
import { OrdersApiClientService } from 'src/app/services/orders.api.client.service';

import { genericComparator } from '../../sorting/sort-utils';
import { OrderGridOptionService } from '../alert-order-column-definition/order-grid-options.service';

@Component({
  selector: 'app-add-watch-order',
  templateUrl: './add-watch-order.component.html',
  styleUrls: ['./add-watch-order.component.scss'],
  standalone: false,
})
export class AddWatchOrderComponent implements OnInit, OnDestroy {
  private data = inject(MAT_DIALOG_DATA);
  private orderOptionsService = inject(OrderGridOptionService);
  private orderApiClientService = inject(OrdersApiClientService);

  @Input() public orders$!: Observable<Order[]>;
  @Input() public showHeader!: boolean;
  public readonly gridOptions: any;

  public watchReference!: Array<string>;
  public subClientReference!: Array<string>;

  public isSubClientRefEnabled!: boolean;
  public isWatchRefEnabled!: boolean;

  public selectionCount!: number;

  protected selectedSubClientRef!: Array<string>;
  protected selectedWatchRef!: Array<string>;

  protected readonly watchReferenceField: string = 'clientReference';
  protected readonly subClientReferenceField: string = 'subClientReference';

  constructor() {
    this.gridOptions = this.orderOptionsService.gridOptions;
    this.watchReference = new Array<string>();
    this.subClientReference = new Array<string>();
    this.selectedWatchRef = new Array<string>();
    this.selectedSubClientRef = new Array<string>();
    this.selectionCount = this.data.orders?.length ?? 0;
  }

  ngOnInit(): void {
    this.orders$.subscribe((orders) => {
      this.loadAllReferences(orders);
      this.restoreSelectedReferences();
      if (this.data.originalId) {
        this.orderApiClientService
          .getOrdersByAlertId(this.data.originalId)
          .subscribe((fetchedOrders) => {
            this.data.orders = fetchedOrders.map((o) => o.orderNumber);
            this.restoreGridSelection();
          });
      }
    });
  }

  private loadAllReferences(orders: Order[]): void {
    const watchRefSet: Set<string> = new Set<string>();
    const subClientRefSet: Set<string> = new Set<string>();
    orders.forEach(({ clientReference, subClientReference }) => {
      if (clientReference) {
        watchRefSet.add(clientReference);
      }
      if (subClientReference) {
        subClientRefSet.add(subClientReference);
      }
    });
    this.watchReference = Array.from(watchRefSet).sort(genericComparator);
    this.subClientReference =
      Array.from(subClientRefSet).sort(genericComparator);
  }

  private restoreSelectedReferences(): void {
    this.selectedWatchRef = this.data?.clientReference ?? [];
    this.selectedSubClientRef = this.data?.subClientReference ?? [];

    this.isWatchRefEnabled = this.selectedWatchRef.length > 0;
    this.isSubClientRefEnabled = this.selectedSubClientRef.length > 0;
  }

  onGridReady(event: GridReadyEvent): void {
    this.orderOptionsService.setGridApi(event.api);
    this.gridOptions.api = event.api;
    this.restoreGridSelection();
  }

  private restoreGridSelection(): void {
    this.gridOptions.api?.forEachNode((node: any) => {
      if (this.data?.orders?.includes(node.data?.orderNumber)) {
        node.setSelected(true);
        if (
          this.data?.clientReference?.includes(node.data?.clientReference) ||
          this.data?.subClientReference?.includes(node.data?.subClientReference)
        ) {
          node.data.selectedFrom = 'dropdown';
        }
      }
    });
    this.refreshRowSelectability();
  }

  onQuickFilterChanged(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.gridOptions.api?.setGridOption('quickFilterText', filterValue);
  }

  onToggleOrderSelection(value: string[], columnField: string): void {
    const isWatchReferenceField = columnField === this.watchReferenceField;
    const otherSelected = isWatchReferenceField
      ? this.selectedSubClientRef
      : this.selectedWatchRef;

    this.gridOptions.api?.forEachNode((node: any) => {
      const reference = isWatchReferenceField
        ? node.data?.clientReference
        : node.data?.subClientReference;
      const otherReference = isWatchReferenceField
        ? node.data?.subClientReference
        : node.data?.clientReference;

      if (value.includes(reference)) {
        node.setSelected(true);
        node.data.selectedFrom = 'dropdown';
      } else if (
        node.data?.selectedFrom &&
        !otherSelected?.includes(otherReference)
      ) {
        node.setSelected(false);
        delete node.data.selectedFrom;
      }
    });

    this.onSelectionChanged();
    this.gridOptions.api?.redrawRows();
  }

  getDisplayText(columnField: string): string {
    const selected =
      columnField === this.watchReferenceField
        ? this.selectedWatchRef
        : this.selectedSubClientRef;
    if (!selected?.length) {
      return '';
    }
    return selected?.length === 1
      ? selected[0]
      : `${selected.length}  Selected References`;
  }

  onWatchReferenceToggleChange(): void {
    this.isWatchRefEnabled = !this.isWatchRefEnabled;
    this.selectedWatchRef = [];

    this.onToggleOrderSelection(
      this.selectedWatchRef,
      this.watchReferenceField,
    );
  }

  onSubClientReferenceToggleChange(): void {
    this.isSubClientRefEnabled = !this.isSubClientRefEnabled;
    this.selectedSubClientRef = [];

    this.onToggleOrderSelection(
      this.selectedSubClientRef,
      this.subClientReferenceField,
    );
  }

  onSelectionChanged(): void {
    const selectedRows = this.gridOptions.api?.getSelectedRows() || [];
    const productIds = [
      ...new Set(selectedRows.map((o: Order) => o.productId)),
    ];

    this.data.orders = selectedRows.map((o: Order) => o.orderNumber);
    this.data.products = productIds.map(
      (productId) => ({ productId } as Product),
    );
    this.data.clientReference = [...this.selectedWatchRef];
    this.data.subClientReference = [...this.selectedSubClientRef];
    this.selectionCount = this.data.orders?.length ?? 0;
    this.refreshRowSelectability();
  }

  private refreshRowSelectability(): void {
    this.gridOptions.api?.setGridOption(
      'isRowSelectable',
      this.isRowSelectable,
    );
  }

  private isRowSelectable = (node: any): boolean => {
    return (
      !this.selectedWatchRef.includes(node.data?.clientReference) &&
      !this.selectedSubClientRef.includes(node.data?.subClientReference)
    );
  };

  ngOnDestroy(): void {
    this.orderOptionsService.clearGridApi();
  }
}
